/**
 *  Isotransformation
 *  from `enriched Text` to HTML element
 *  and from HTML element to `enriched Text`
 */
import React from 'react';
// TODO had some build issues
import ReactDomServer from 'react-dom/server';
import config from 'config/config';

const smileysMap = new Map([
  [':)', 'smile.gif'],
  [':-)', 'smile.gif'],
  [';)', 'wink.gif'],
  [';-)', 'wink.gif'],
  [':d', 'bigsmile.gif'],
  [':-d', 'bigsmile.gif'],
  [':(', 'sad.gif'],
  [':-(', 'sad.gif'],
  [':x', 'angry.gif'],
  [':|', 'confuse.gif'],
  [':-/', 'confuse.gif'],
  [':p', 'funny.gif'],
  [':-p', 'funny.gif'],
  [':o', 'surprise.gif'],
  [':-o', 'surprise.gif'],
  ['8-)', 'cool.gif'],
  [':\'(', 'cry.png'],
]);

const parser = {
  /**
   * Return an inline links to the user's profile
   * @param { userId, username } user's Data
   * @param editable boolean
   *
   */
  getMention: function getMention({ userId, username }, editable = false, disableLink = false):Object {
    if (disableLink) {
      return username;
    }

    return <span key={`mention-${userId}`} data-link-container={true}>
      {
        editable ?
          <a contentEditable="false" rel="nofollow" data-id={userId}>{username}</a> :
          <a rel="nofollow" href={'/user/' + username} data-id={userId}>{username}</a>
      }
    </span>;
  },

  /**
   * Return an inline links to the an URL
   * @param { url } URL
   * @param editable boolean
   *
   */
  getLink: function getLink(
    { url, originalText, text, isWhiteList, youtubeID },
    editable = false,
    disableLink = false):Object {
    if (editable) {
      return originalText;
    }
    if (disableLink) {
      return text;
    }
    if (youtubeID) {
      const formattedYoutubeID = this.formatYoutubeID(youtubeID);
      return <div className="youtubeVideoContainer">
        <iframe
          src={`//www.youtube.com/embed/${formattedYoutubeID}`}
          width="480"
          height="270"
          frameBorder="0"
        />
      </div>;
    }
    const anchorProps = { href: url, rel: 'nofollow', target: '_blank' };
    if (isWhiteList) {
      delete anchorProps.rel;
    }

    return <a key={`link-${Math.random()}`} {...anchorProps}>{text}</a>;
  },

  /**
   * Format the youtube ID to a valid '/embed/' format e.g. bMYCn-wR80?start=144
   * @param youtubeID
   */
  formatYoutubeID: youtubeID => {
    if (youtubeID.indexOf('?') === -1) return youtubeID;
    const params = youtubeID.substr(youtubeID.indexOf('?') + 1);
    return youtubeID.replace(params, params.replace('s', '').replace('t=', 'start='));
  },

  /**
   * Return styled text
   * @param { text, style } text and style to display
   * @param editable boolean
   *
   */
  getText: function getText({ text, style }, editable = false):Object {
    let render = text;
    if (style.u) {
      render = <u>{render}</u>;
    }
    if (style.i) {
      render = editable ? <i>{render}</i> : <em>{render}</em>;
    }
    if (style.b) {
      render = editable ? <b>{render}</b> : <strong>{render}</strong>;
    }
    return render;
  },

  /**
   * Returns smiley image
   * @param { originalText } originalText containing smiley
   * @param editable boolean
   *
   */
  getSmiley: function getSmiley({ originalText }, editable = false, index):Object {
    const smileyChars = originalText.toLowerCase();

    if(editable || !smileysMap.has(smileyChars)) {
      return originalText;
    }

    const fileName = smileysMap.get(smileyChars);
    return <img
      key={`smiley-${originalText}-${index}`}
      src={config.cdn.smiley + fileName}
      alt={originalText}
      title={originalText}
    />;
  },

  /**
   * add key to an nested arrays of elements
   * @param {item} an array or an react element
   * @param {index} index of the iterable
   *
   */
  addKeys: function(item, index) {
    if (item.map) {
      return item.map(parser.addKeys);
    }

    return item._isReactElement ? React.cloneElement(item, { key: index }) : item;
  },

  /**
   * Return an element to display
   * @param {enrichedText} enriched text
   *
   */
  getElement: function getElement(enrichedText:Array = [], editable = false, disableLink = false):Object {
    if (!enrichedText && !enrichedText.length) {
      return null;
    }
    return enrichedText
      .reduce(([lastParagraph, ...paragraphs], item, index) => {
        switch (item.type) {
          case 'mention':
            return [[...lastParagraph, parser.getMention(item, editable, disableLink)], ...paragraphs];
          case 'smiley':
            return [[...lastParagraph, parser.getSmiley(item, editable, index)], ...paragraphs];
          case 'link':
            const link = parser.getLink(item, editable, disableLink);
            return link.type === 'div' ?
              [[], link, lastParagraph, ...paragraphs] :
              [[...lastParagraph, link], ...paragraphs];
          case 'return':
            return [[], lastParagraph, ...paragraphs];
          case 'text':
            return [[...lastParagraph, parser.getText(item, editable)], ...paragraphs];
          default:
            return [lastParagraph, ...paragraphs];
        }
      }, [[]]).reverse()
      .map(parser.addKeys)
      .map((paragraph, index) => paragraph.type === 'div' ?
        React.cloneElement(paragraph, { key: 'p-' + index + Math.random() }) :
        <p key={'p-' + index + Math.random()}>{paragraph}</p>);
  },

  /**
   * Return an description for SEO purpose
   * @param {enrichedText} enriched text
   *
   */
  getSeoDescription: function getSeoDescription(enrichedText:Array):Object {
    return enrichedText
      .reduce((description, item) => {
        switch (item.type) {
          case 'mention':
            return description + item.username;
          case 'text':
          case 'link':
            return description + item.text;
          case 'return':
            return description + ' ';
        }
      }, '');
  },

  /**
   * Return an HTML for content editor
   * @param {enrichedText} enriched text
   *
   */
  getEditableHTML: function getEditableHTML(enrichedText:Array):Object {
    return this.getElement(enrichedText, true).reduce(
      (editableHTML, paragraph) => editableHTML + ReactDomServer.renderToStaticMarkup(paragraph),
      '',
    );
  },

  getCleanNodeValue(node) {
    let safeNodeValue = node.nodeValue || '';
    safeNodeValue = safeNodeValue.replace(config.regex.MENTIONED_USERS_ID, '@ [$1]');
    config.regex.UNDESIRABLE_CHARACTERS.forEach(el =>
      safeNodeValue = safeNodeValue.replace(el.pattern, el.replacement)
    );
    return safeNodeValue;
  },

  // rough polyfill of innerText to have a homogeneous behaviour
  parseNode: function(node) {
    let str = '';
    const mentionedUserIds = {};
    const childNodes = Array.from(node.childNodes);

    const recursive = (subNode, before, after) => {
      const { str: innerStr, mentionedUserIds: innerMentionedUserIds} = this.parseNode(subNode);
      Object.assign(mentionedUserIds, innerMentionedUserIds);
      str += `${before}${innerStr}${after}`;
    };

    childNodes.forEach((child) => {
      switch (child.nodeName) {
        // escape mentions or links
        case 'A':
          const id = child.getAttribute('data-id');
          if (!id) {
            recursive(child, '', '');
            break;
          }
          str += `@[${id}]`;
          mentionedUserIds[id] = true;
          break;
        // replace unwanted mentions format
        // we use the same formatting than FB
        case '#text':
          str += this.getCleanNodeValue(child);
          break;
        case 'BR':
          if (node.lastChild === child && (node.nodeName === 'DIV' || node.nodeName === 'P')) {
            break;
          }
          str += '\n';
          break;
        case 'DIV':
        case 'P':
          recursive(child, '', '\n');
          break;
        case 'U':
          recursive(child, '[u]', '[/u]');
          break;
        case 'I':
        case 'EM':
          recursive(child, '[i]', '[/i]');
          break;
        case 'B':
        case 'STRONG':
          recursive(child, '[b]', '[/b]');
          break;
        default:
          recursive(child, '', '');
          break;
      }
    });
    return { str, mentionedUserIds };
  },
};

module.exports = parser;



// WEBPACK FOOTER //
// ./src/utils/parserHTML.js